/**
 * API Service for Community AI Chat
 */

const API_BASE = '/api';

/**
 * Send chat message
 */
export async function sendChatMessage({ message, conversationId, businessSlug, contextDepth }) {
  const response = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message,
      conversationId,
      businessSlug,
      contextDepth
    }),
  });

  if (!response.ok) {
    throw new Error('Chat request failed');
  }

  return response.json();
}

/**
 * Search businesses
 */
export async function searchBusinesses(query) {
  const response = await fetch(`${API_BASE}/businesses/search?q=${encodeURIComponent(query)}`);
  
  if (!response.ok) {
    throw new Error('Search failed');
  }

  return response.json();
}

/**
 * Get business details
 */
export async function getBusinessDetails(slug) {
  const response = await fetch(`${API_BASE}/businesses/${slug}`);
  
  if (!response.ok) {
    throw new Error('Failed to load business');
  }

  return response.json();
}

/**
 * Compare businesses
 */
export async function compareBusinesses(businessSlugs, compareField = 'menu') {
  const response = await fetch(`${API_BASE}/businesses/compare`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      businesses: businessSlugs,
      compareField
    }),
  });

  if (!response.ok) {
    throw new Error('Comparison failed');
  }

  return response.json();
}

/**
 * Process image with vision
 */
export async function processImage(imageFile, question) {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('question', question);

  const response = await fetch(`${API_BASE}/vision`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Image processing failed');
  }

  return response.json();
}

/**
 * Get community info
 */
export async function getCommunityInfo() {
  const response = await fetch(`${API_BASE}/community`);
  
  if (!response.ok) {
    throw new Error('Failed to load community info');
  }

  return response.json();
}

/**
 * WebSocket connection for real-time features
 */
export function connectWebSocket(onMessage) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);

  ws.onopen = () => {
    console.log('WebSocket connected');
  };

  ws.onmessage = (event) => {
    const data = JSON.parse(event.data);
    onMessage(data);
  };

  ws.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  return ws;
}

/**
 * Parse business mentions in text
 */
export function parseBusinessMentions(text, businesses) {
  const mentions = [];
  
  businesses.forEach(business => {
    const regex = new RegExp(`\\b${business.name}\\b`, 'gi');
    if (regex.test(text)) {
      mentions.push(business);
    }
  });

  return mentions;
}

/**
 * Format price for display
 */
export function formatPrice(price) {
  if (typeof price === 'number') {
    return `$${price.toFixed(2)}`;
  }
  return price;
}

/**
 * Format business hours
 */
export function formatHours(hours) {
  if (!hours) return 'Hours not available';
  
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const todayHours = hours[today];
  
  if (todayHours) {
    return `Today: ${todayHours}`;
  }
  
  return 'Closed today';
}